import type { CSSProperties, ReactNode, SVGProps } from "react";
import {
  resolveLightingModeIconId,
  type LightingIconArea,
  type LightingIconProvider,
  type LightingModeIconId,
} from "./lightingModeIconMap";

/** 包裹层 class，配合灯效页 SCSS */
export const LIGHTING_MODE_ICON_CLASS = "lighting-mode-icon";

const S = 24;
const W = 1.75;

const L = {
  stroke: "currentColor",
  strokeWidth: W,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  fill: "none" as const,
};

function Svg(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={S}
      height={S}
      viewBox={`0 0 ${S} ${S}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      {...props}
    />
  );
}

function G({ children }: { children?: ReactNode }) {
  return <Svg>{children}</Svg>;
}

/** 小键帽（按压/涟漪类灯效） */
function Key({ x = 8.5, y = 12 }: { x?: number; y?: number }) {
  return (
    <>
      <rect {...L} x={x} y={y} width="7" height="5.5" rx="1.2" />
      <path {...L} d={`M${x + 3.5} ${y - 2.5}v2`} />
    </>
  );
}

const GLYPHS: Record<LightingModeIconId, ReactNode> = {
  waveHorizontal: (
    <G>
      <path
        {...L}
        d="M5 9c1.5 2 2.5 2 4 0s2.5-2 4 0 2.5 2 4 0M5 15c1.5 2 2.5 2 4 0s2.5-2 4 0 2.5 2 4 0"
      />
    </G>
  ),
  waveVertical: (
    <G>
      <path
        {...L}
        d="M9 5c2 1.5 2 2.5 0 4s-2 2.5 0 4 2 2.5 0 4M15 5c2 1.5 2 2.5 0 4s-2 2.5 0 4 2 2.5 0 4"
      />
    </G>
  ),
  spreadCenter: (
    <G>
      <circle
        {...L}
        cx="12"
        cy="12"
        r="1.5"
        fill="currentColor"
        stroke="none"
      />
      <circle {...L} cx="12" cy="12" r="4.5" />
      <circle {...L} cx="12" cy="12" r="7.5" opacity={0.45} />
    </G>
  ),
  rippleWave: (
    <G>
      <path {...L} d="M4 14c2.5-2 5-2 7.5 0s5 2 7.5 0" />
      <path {...L} d="M6 12h3M15 12h3" opacity={0.7} />
      <path {...L} d="M16 12l2-1.5M16 12l2 1.5" />
    </G>
  ),
  starTwinkle: (
    <G>
      <path
        {...L}
        d="M12 5l1.2 3.6h3.8l-3.1 2.2 1.2 3.6L12 12.2 8.9 14.4l1.2-3.6-3.1-2.2h3.8z"
      />
    </G>
  ),
  starField: (
    <G>
      <circle fill="currentColor" stroke="none" cx="7" cy="8" r="1" />
      <circle fill="currentColor" stroke="none" cx="12" cy="6" r="1.2" />
      <circle fill="currentColor" stroke="none" cx="17" cy="9" r="0.9" />
      <circle fill="currentColor" stroke="none" cx="9" cy="14" r="0.8" />
      <circle fill="currentColor" stroke="none" cx="15" cy="15" r="1" />
    </G>
  ),
  breathing: (
    <G>
      <circle {...L} cx="12" cy="12" r="3.5" />
      <circle
        {...L}
        cx="12"
        cy="12"
        r="6.5"
        strokeDasharray="3 4"
        opacity={0.65}
      />
    </G>
  ),
  spectrumCycle: (
    <G>
      <path {...L} d="M5 15a7 7 0 0114 0" />
      <path {...L} d="M7.5 12a4.5 4.5 0 019 0" opacity={0.75} />
      <path {...L} d="M10 9a2 2 0 014 0" opacity={0.5} />
    </G>
  ),
  disco: (
    <G>
      <circle {...L} cx="12" cy="12" r="2" />
      <path
        {...L}
        d="M12 4v2M12 18v2M4 12h2M18 12h2M6 6l1.5 1.5M16.5 16.5L18 18M6 18l1.5-1.5M16.5 7.5L18 6"
      />
    </G>
  ),
  staticOn: (
    <G>
      <circle
        {...L}
        cx="12"
        cy="12"
        r="3.5"
        fill="currentColor"
        fillOpacity={0.15}
      />
      <circle {...L} cx="12" cy="12" r="3.5" />
      <path {...L} d="M12 4v2M12 18v2M4 12h2M18 12h2" />
    </G>
  ),
  linearGradient: (
    <G>
      <path {...L} d="M6 18L18 6" />
      <path {...L} d="M9 18l9-9" opacity={0.55} />
      <path {...L} d="M12 18l6-6" opacity={0.35} />
    </G>
  ),
  verticalLift: (
    <G>
      <path {...L} d="M9 16V8M15 8v8" />
      <path {...L} d="M7.5 10.5L9 9l1.5 1.5M16.5 13.5L15 15l-1.5-1.5" />
    </G>
  ),
  bounceBack: (
    <G>
      <path {...L} d="M5 12h9" />
      <path {...L} d="M14 12l-2.5-2.5M14 12l-2.5 2.5" />
      <path {...L} d="M18 9v6" opacity={0.5} />
    </G>
  ),
  whirlCenter: (
    <G>
      <path {...L} d="M12 6a6 6 0 016 6M12 18a6 6 0 01-6-6" />
      <path {...L} d="M15 7.5l-1.5 2.5h3zM9 16.5l1.5-2.5h-3z" />
    </G>
  ),
  stormSpin: (
    <G>
      <path {...L} d="M12 5c3.5 0 6 2.5 6 6s-2.5 6-6 6" />
      <path {...L} d="M14 6.5l2-1.5v3.5z" />
      <circle {...L} cx="12" cy="11" r="1.5" />
    </G>
  ),
  diagonalStream: (
    <G>
      <path {...L} d="M5 18l5-5M9 18l5-5" />
      <circle fill="currentColor" stroke="none" cx="17" cy="7" r="1.2" />
    </G>
  ),
  laserRain: (
    <G>
      <path {...L} d="M5 7h14" opacity={0.6} />
      <path {...L} d="M8 9l3 9M12 8l3 10M16 9l3 9" />
    </G>
  ),
  sparkTwinkle: (
    <G>
      <circle fill="currentColor" stroke="none" cx="12" cy="12" r="2" />
      <path {...L} d="M12 5v3M12 16v3M5 12h3M16 12h3" />
    </G>
  ),
  sparkleArc: (
    <G>
      <path {...L} d="M5 14a7 7 0 0114 0" />
      <path {...L} d="M8 11l4-2 4 2M10 8l2-2 2 2" opacity={0.7} />
    </G>
  ),
  fireworks: (
    <G>
      <path
        {...L}
        d="M12 11V5M12 11l-5 2.5M12 11l5 2.5M12 11l-3.5 6M12 11l3.5 6"
      />
    </G>
  ),
  triangleBounce: (
    <G>
      <path {...L} d="M8 16l4-3.5 4 3.5M8 12.5l4-3.5 4 3.5M8 9l4-3.5 4 3.5" />
    </G>
  ),
  staticRipple: (
    <G>
      <circle
        {...L}
        cx="12"
        cy="11"
        r="2"
        fill="currentColor"
        fillOpacity={0.2}
      />
      <circle {...L} cx="12" cy="11" r="5" />
      <circle {...L} cx="12" cy="11" r="7.5" opacity={0.4} />
    </G>
  ),
  keyRipple: (
    <G>
      <Key y={13} />
      <circle {...L} cx="12" cy="10" r="3" />
      <circle {...L} cx="12" cy="10" r="5.5" opacity={0.45} />
    </G>
  ),
  keyTrail: (
    <G>
      <Key x={6} y={14} />
      <ellipse {...L} cx="11" cy="12" rx="3" ry="1.2" opacity={0.5} />
      <ellipse {...L} cx="15" cy="9" rx="3" ry="1.2" opacity={0.75} />
    </G>
  ),
  keyFireworks: (
    <G>
      <Key y={14} />
      <path
        {...L}
        d="M12 6l1.5 3 3 .5-2.2 2  .6 3-2.9-1.6-2.9 1.6.6-3-2.2-2 3-.5z"
      />
    </G>
  ),
  keyLightColumn: (
    <G>
      <rect {...L} x="9.5" y="11" width="5" height="6" rx="1" />
      <path {...L} d="M7.5 10v8M16.5 10v8" opacity={0.7} />
    </G>
  ),
  off: (
    <G>
      <path {...L} d="M8 6h8l4 6H6l2-6z" />
      <path {...L} d="M6 6l12 12" />
    </G>
  ),
  keyPressOn: (
    <G>
      <Key y={11} />
      <circle fill="currentColor" stroke="none" cx="12" cy="8" r="1.5" />
    </G>
  ),
  keyReleaseOff: (
    <G>
      <rect
        {...L}
        x="8.5"
        y="12"
        width="7"
        height="5.5"
        rx="1.2"
        opacity={0.35}
      />
      <path {...L} d="M7 7l10 10" />
    </G>
  ),
  snow: (
    <G>
      <path {...L} d="M12 5v14M5 12h14M7.5 7.5l9 9M16.5 7.5l-9 9" />
    </G>
  ),
  snowFall: (
    <G>
      <path {...L} d="M6 6h12" opacity={0.5} />
      <path {...L} d="M8 8v8M14 7v9M18 9v7" />
      <circle fill="currentColor" stroke="none" cx="8" cy="10" r="0.8" />
      <circle fill="currentColor" stroke="none" cx="14" cy="12" r="0.8" />
    </G>
  ),
  footprintSnow: (
    <G>
      <ellipse {...L} cx="9" cy="15" rx="2" ry="1" />
      <ellipse {...L} cx="12" cy="13" rx="2" ry="1" />
      <ellipse {...L} cx="15" cy="11" rx="2" ry="1" />
      <path {...L} d="M5 7c0-1.5 1.5-2.5 3-2.5s3 1 3 2.5" opacity={0.5} />
    </G>
  ),
  blossom: (
    <G>
      <circle
        {...L}
        cx="12"
        cy="12"
        r="2"
        fill="currentColor"
        fillOpacity={0.25}
      />
      <path {...L} d="M12 6v2.5M12 15.5V18M6 12h2.5M15.5 12H18" />
      <path
        {...L}
        d="M8 8l1.8 1.8M14.2 14.2 16 16M16 8l-1.8 1.8M8 16l1.8-1.8"
      />
    </G>
  ),
  spring: (
    <G>
      <path {...L} d="M7 16c0-3.5 2-5.5 5-5.5s5 2 5 5.5" />
      <path {...L} d="M9.5 10.5V7M14.5 10.5V7" />
      <circle fill="currentColor" stroke="none" cx="12" cy="16" r="1" />
    </G>
  ),
  colorfulGrid: (
    <G>
      <path {...L} d="M5 5h5v5H5zM14 5h5v5h-5zM5 14h5v5H5zM14 14h5v5h-5z" />
    </G>
  ),
  peakTurn: (
    <G>
      <path {...L} d="M4 16h5l2.5-5 2.5 5h5" />
      <circle fill="currentColor" stroke="none" cx="8" cy="8" r="1.2" />
    </G>
  ),
  instant: (
    <G>
      <path {...L} d="M13 5L7 13h4.5l-1 6 6.5-9h-4.5z" />
    </G>
  ),
  doubleHit: (
    <G>
      <circle {...L} cx="9" cy="12" r="2.8" />
      <circle {...L} cx="15" cy="12" r="2.8" />
      <path {...L} d="M11.2 12h1.6" />
    </G>
  ),
  ripple: (
    <G>
      <circle {...L} cx="12" cy="12" r="2" />
      <circle {...L} cx="12" cy="12" r="5" />
      <circle {...L} cx="12" cy="12" r="7.5" opacity={0.35} />
    </G>
  ),
  flow: (
    <G>
      <path {...L} d="M4 12h11" />
      <path {...L} d="M13 9l4 3-4 3" />
      <path {...L} d="M7 9l3 3-3 3" opacity={0.45} />
    </G>
  ),
  mountain: (
    <G>
      <path {...L} d="M3 18h18M7 18l5-9 5 9M9.5 14h5" />
    </G>
  ),
  shuttle: (
    <G>
      <path {...L} d="M5 12h14" />
      <path {...L} d="M7.5 9.5L5 12l2.5 2.5M16.5 14.5L19 12l-2.5-2.5" />
    </G>
  ),
  heartbeat: (
    <G>
      <path
        {...L}
        d="M12 17.5c-4-2.8-6.5-5.5-6.5-8.2a3.2 3.2 0 016 0 3.2 3.2 0 016 0c0 2.7-2.5 5.4-6.5 8.2z"
      />
    </G>
  ),
  custom: (
    <G>
      <rect {...L} x="5" y="5" width="14" height="14" rx="1.5" />
      <path {...L} d="M8 15l2.5-3.5 2 2L16 9" />
    </G>
  ),
  neon: (
    <G>
      <rect {...L} x="5" y="9" width="14" height="6" rx="2" />
      <path {...L} d="M8 12h8" strokeWidth={2} />
      <path {...L} d="M5 15h14" opacity={0.25} />
    </G>
  ),
  colorfulCloud: (
    <G>
      <path {...L} d="M6 15h12" />
      <path {...L} d="M7.5 15a4.5 4.5 0 019 0" />
      <path {...L} d="M9.5 11.5a2.5 2.5 0 015 0" opacity={0.55} />
    </G>
  ),
  flying: (
    <G>
      <path {...L} d="M4 15l5-2.5 5 2.5-5 2.5z" />
      <path {...L} d="M11 10l5-2 4 2-4 2z" opacity={0.65} />
    </G>
  ),
  traverse: (
    <G>
      <path {...L} d="M6 12h12" />
      <path {...L} d="M15 9.5l3 2.5-3 2.5M9 14.5l-3-2.5 3-2.5" />
    </G>
  ),
  scan: (
    <G>
      <path {...L} d="M6 9h12M6 12h12M6 15h12" />
      <path {...L} d="M14 7v10" strokeDasharray="2 2" />
    </G>
  ),
  scanHorizontal: (
    <G>
      <path {...L} d="M5 12h14" />
      <path {...L} d="M7 9.5l-2.5 2.5L7 14.5M17 14.5l2.5-2.5L17 9.5" />
      <path {...L} d="M5 8v8M19 8v8" opacity={0.35} />
    </G>
  ),
  tide: (
    <G>
      <path {...L} d="M4 13c2.5-2 5-2 7.5 0s5 2 7.5 0" />
      <path {...L} d="M7 16l2.5-2M17 16l-2.5-2" opacity={0.65} />
    </G>
  ),
  rainbow: (
    <G>
      <path {...L} d="M5 15a7 7 0 0114 0" />
      <path {...L} d="M7.5 12.5a4.5 4.5 0 019 0" />
      <path {...L} d="M10 10a2 2 0 014 0" />
    </G>
  ),
  swirl: (
    <G>
      <path {...L} d="M8 12a4 4 0 018 0 4 4 0 01-8 0" />
      <path {...L} d="M12 8v8" opacity={0.35} />
      <path {...L} d="M14 9l2-1v2.5z" />
    </G>
  ),
  glow: (
    <G>
      <circle
        {...L}
        cx="12"
        cy="12"
        r="2.5"
        fill="currentColor"
        fillOpacity={0.2}
      />
      <path
        {...L}
        d="M12 4v2M12 18v2M4 12h2M18 12h2M6.5 6.5l1.5 1.5M16 16l1.5 1.5"
      />
    </G>
  ),
  pane: (
    <G>
      <path {...L} d="M5 5h7v7H5zM14 5h5v14h-5zM5 14h7v5H5z" />
    </G>
  ),
  sineWave: (
    <G>
      <path {...L} d="M4 12c2-3 3.5-3 5.5 0s3.5 3 5.5 0 3.5-3 5.5 0" />
    </G>
  ),
  mosaic: (
    <G>
      <path
        {...L}
        d="M5 5h3.5v3.5H5zM10 5h3.5v3.5H10zM15 5h3.5v3.5H15zM7.5 10h3.5v3.5H7.5zM12.5 10h3.5v3.5h-3.5zM5 15h3.5v3.5H5zM15 15h3.5v3.5H15z"
      />
    </G>
  ),
  rain: (
    <G>
      <path {...L} d="M5 7h14" opacity={0.55} />
      <path {...L} d="M8 9l-1.5 8M12 8l-1.5 9M16 10l-1.5 7" />
    </G>
  ),
  morph: (
    <G>
      <rect {...L} x="5" y="8" width="6" height="6" rx="1" />
      <rect {...L} x="13" y="10" width="6" height="6" rx="1" opacity={0.55} />
      <path {...L} d="M11 11l2 2" />
    </G>
  ),
  waterRipple: (
    <G>
      <path {...L} d="M3 14c3-2.5 6-2.5 9 0s6 2.5 9 0" />
      <path {...L} d="M3 17c3-2.5 6-2.5 9 0s6 2.5 9 0" opacity={0.45} />
    </G>
  ),
  shadow: (
    <G>
      <ellipse {...L} cx="9" cy="15" rx="3.5" ry="1.5" />
      <path {...L} d="M12 8l6-2" />
      <circle fill="currentColor" stroke="none" cx="18" cy="6" r="1.2" />
    </G>
  ),
  meteor: (
    <G>
      <circle fill="currentColor" stroke="none" cx="7" cy="7" r="2" />
      <path {...L} d="M9 9l9 9" />
    </G>
  ),
  train: (
    <G>
      <rect {...L} x="3" y="10" width="18" height="5" rx="1" />
      <path {...L} d="M3 13h18" opacity={0.35} />
      <circle {...L} cx="7" cy="17.5" r="1.3" />
      <circle {...L} cx="17" cy="17.5" r="1.3" />
    </G>
  ),
  chasing: (
    <G>
      <path {...L} d="M5 12h14" opacity={0.3} />
      <circle fill="currentColor" stroke="none" cx="8" cy="12" r="1.5" />
      <circle {...L} cx="12" cy="12" r="1.5" />
      <circle {...L} cx="16" cy="12" r="1.5" opacity={0.45} />
    </G>
  ),
  shrink: (
    <G>
      <path {...L} d="M8 8l8 8M16 8L8 16" />
      <circle {...L} cx="12" cy="12" r="2.5" />
    </G>
  ),
  marquee: (
    <G>
      <rect
        {...L}
        x="5"
        y="10"
        width="3"
        height="4"
        rx="0.5"
        fill="currentColor"
        fillOpacity={0.2}
      />
      <path {...L} d="M5 12h14" />
      <path {...L} d="M16 9.5l2.5 2.5-2.5 2.5" />
    </G>
  ),
  crossLight: (
    <G>
      <path {...L} d="M12 6v12M6 12h12" />
      <circle {...L} cx="12" cy="12" r="3" opacity={0.5} />
    </G>
  ),
  singleOn: (
    <G>
      <rect {...L} x="9" y="10" width="6" height="5" rx="1" opacity={0.4} />
      <circle fill="currentColor" stroke="none" cx="12" cy="12" r="2" />
    </G>
  ),
  circles: (
    <G>
      <circle {...L} cx="12" cy="12" r="6.5" />
      <circle {...L} cx="12" cy="12" r="3.5" />
    </G>
  ),
  rowLight: (
    <G>
      <rect
        {...L}
        x="6"
        y="11"
        width="3"
        height="3"
        rx="0.5"
        fill="currentColor"
        fillOpacity={0.25}
      />
      <rect {...L} x="10.5" y="11" width="3" height="3" rx="0.5" />
      <rect
        {...L}
        x="15"
        y="11"
        width="3"
        height="3"
        rx="0.5"
        fill="currentColor"
        fillOpacity={0.25}
      />
    </G>
  ),
  centerCollision: (
    <G>
      <path {...L} d="M6 12h12M12 6v12" />
      <circle fill="currentColor" stroke="none" cx="12" cy="12" r="2" />
    </G>
  ),
  windmill: (
    <G>
      <path {...L} d="M12 5v5M12 14v5M5 12h5M14 12h5" />
      <circle {...L} cx="12" cy="12" r="2" />
    </G>
  ),
  waterfall: (
    <G>
      <path {...L} d="M7 6v12M12 5v14M17 7v11" opacity={0.85} />
      <path {...L} d="M5 18h14" />
    </G>
  ),
  dreamRainbow: (
    <G>
      <path {...L} d="M5 14a7 7 0 0114 0" />
      <path {...L} d="M8 11c1.5-2 4-2 5.5 0" opacity={0.6} />
    </G>
  ),
};

export function LightingModeIcon({
  provider,
  mode,
  labelKey,
  area = "Keyboard",
  className,
  style,
}: {
  provider: LightingIconProvider;
  mode: number;
  labelKey?: string;
  area?: LightingIconArea;
  className?: string;
  style?: CSSProperties;
}) {
  const id = resolveLightingModeIconId(provider, mode, { labelKey, area });
  return (
    <span
      className={[LIGHTING_MODE_ICON_CLASS, className]
        .filter(Boolean)
        .join(" ")}
      style={{
        display: "inline-flex",
        flexShrink: 0,
        alignItems: "center",
        justifyContent: "center",
        width: 28,
        height: 28,
        ...style,
      }}
    >
      {GLYPHS[id]}
    </span>
  );
}

export type LightingModeOptionWithIcon<T extends number = number> = {
  label: string;
  value: T;
  icon: ReactNode;
  labelKey?: string;
};

export function mapLightingOptionsWithIcons<
  T extends { label: string; value: number; labelKey?: string },
>(
  options: T[],
  provider: LightingIconProvider,
  area: LightingIconArea = "Keyboard"
): Array<T & { icon: ReactNode }> {
  return options.map((item) => ({
    ...item,
    icon: (
      <LightingModeIcon
        provider={provider}
        mode={item.value}
        labelKey={item.labelKey}
        area={area}
      />
    ),
  }));
}

export {
  resolveLightingModeIconId,
  LIGHTING_MODE_BUTTON_GROUP_CLASS,
  type LightingIconProvider,
  type LightingIconArea,
  type LightingModeIconId,
} from "./lightingModeIconMap";
